import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  InputAdornment,
  IconButton,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Stepper,
  Step,
  StepLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material'
import { Add, Delete } from '@material-ui/icons'
import { toCents, fromCents } from '../../moneyUtils'
import {
  buildRelationshipExpression,
  decompileTarget
} from './benefitRatingCodec'
import { describeRatingNode } from './ratingNodeSummary'

// Benefit creation + edit — see benefitRatingCodec.js and
// docs/2026-09-04-benefit-pricing-wizard-design.md §9. Opens unconditionally
// for both; a target with a hand-built (non-wizard-shaped) rating config is
// shown flat/blank with a warning (editState.notDecompilable) rather than
// falling back to a separate editor.

const RELATIONSHIP_TYPES = [
  'Principal',
  'Spouse',
  'Child',
  'Parent',
  'Parent_In_Law',
  'Beneficiary_Adult',
  'Husband',
  'Wife',
  'Daughter',
  'Son',
  'Mother',
  'Father',
  'Mother_In_Law',
  'Father_In_Law',
  'Uncle',
  'Aunt',
  'Niece',
  'Nephew',
  'Cousin',
  'Grandparent',
  'Grandchild',
  'Step_Father',
  'Step_Mother',
  'Step_Child',
  'Fiance'
]

const BENEFIT_TYPES = [
  'Cashback',
  'Death',
  'Hospital',
  'Property',
  'Motor',
  'Income_Loss',
  'Personal_Accident',
  'Medical_Expense',
  'Fixed_Indemnity_Generic_Details',
  'Variable_Indemnity_Generic_Details',
  'Funeral',
  'Inpatient',
  'Outpatient',
  'Maternity'
]

const DEFAULT_NEW_REL = {
  relationship: '',
  min_age: '',
  max_age: '',
  max_count: ''
}

const slug = s =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

// Deterministic, always-unique benefit_code — §5.4. Regenerated whenever the
// benefit's own guid changes (it doesn't exist until the RatingNode chain
// needs one, so this is computed once per submit, not per render).
const makeBenefitCode = (name, guid) =>
  `${slug(name) || 'benefit'}-${guid.slice(0, 8)}`

const uid = () =>
  Math.random()
    .toString(36)
    .slice(2, 10)

const fmtVal = value =>
  value === null || value === undefined ? '' : String(value)

// Reconstructs the wizard's full initial state from an existing benefit —
// §9's edit mode (revised, §9.2: opens unconditionally, no separate routing
// check). A target that isn't wizard-shaped is prefilled flat/blank and
// listed in the returned `notDecompilable`, driving a warning rather than
// blocking the wizard from opening at all.
const deriveEditState = (benefit, ratingNodes, ratingTables) => {
  const cover = decompileTarget(
    benefit.cover_rating_node_guid,
    ratingNodes,
    ratingTables,
    'cover',
    'amount'
  )
  const waiting = decompileTarget(
    benefit.waiting_period_rating_node_guid,
    ratingNodes,
    ratingTables,
    'waiting_period',
    'cardinality'
  )
  const premium = decompileTarget(
    benefit.premium_rating_node_guid,
    ratingNodes,
    ratingTables,
    'premium',
    'amount'
  )
  const hasPremium =
    (benefit.premium_amount_in_cents !== undefined &&
      benefit.premium_amount_in_cents !== null) ||
    Boolean(benefit.premium_rating_node_guid)

  // The wizard opens unconditionally for editing now — no more falling back
  // to a separate classic form for a hand-built target (§9.2, revised). A
  // target that can't be decompiled is shown as flat/blank here regardless,
  // so this list drives a warning telling the user their existing (untouched
  // unless they edit that stage) rating config isn't reflected above.
  const notDecompilable = []
  if (benefit.cover_rating_node_guid && !cover.decompilable)
    notDecompilable.push('Benefit Amount')
  if (benefit.waiting_period_rating_node_guid && !waiting.decompilable)
    notDecompilable.push('Waiting Period')
  if (benefit.premium_rating_node_guid && !premium.decompilable)
    notDecompilable.push('Premium')

  // Read-only, general-purpose summaries (ratingNodeSummary.js) for whatever
  // isn't decompilable — this can describe ANY table_type/dimensions/
  // expression combo, unlike decompileTarget's strict wizard-shape check, so
  // it's the one place the user can actually see what's really configured.
  const findNode = guid => (ratingNodes || []).find(n => n.guid === guid)
  const summaries = {}
  if (benefit.cover_rating_node_guid && !cover.decompilable) {
    summaries.cover = describeRatingNode(
      findNode(benefit.cover_rating_node_guid),
      ratingTables,
      'money'
    )
  }
  if (benefit.waiting_period_rating_node_guid && !waiting.decompilable) {
    summaries.waiting_period = describeRatingNode(
      findNode(benefit.waiting_period_rating_node_guid),
      ratingTables,
      'waiting_period'
    )
  }
  if (benefit.premium_rating_node_guid && !premium.decompilable) {
    summaries.premium = describeRatingNode(
      findNode(benefit.premium_rating_node_guid),
      ratingTables,
      'money'
    )
  }

  return {
    notDecompilable,
    summaries,
    name: benefit.name || '',
    benefitType: benefit.benefit_type || '',
    allowedRelationships: (benefit.allowed_relationships || []).map(r => ({
      relationship: r.relationship || '',
      min_age: fmtVal(r.min_age),
      max_age: fmtVal(r.max_age),
      max_count: fmtVal(r.max_count)
    })),
    benefitCardinality: fmtVal(benefit.benefit_cardinality),
    benefitGranularity: benefit.benefit_granularity || 'Month',
    // Effective mode for the UI: a genuinely decompiled target uses its real
    // mode + rules; a non-decompilable one still pre-selects `probableMode`
    // (per_rule if any table is linked at all, matching your correction —
    // "auto-select relationship per rule if different table per rule" — even
    // though the specific rules can't be shown) but with NO tableGuid, so
    // planEditTarget (below) can tell "hinted, untouched" apart from a real
    // decompiled table and never targets updateRatingTable at unknown content.
    cover:
      cover.mode === 'per_rule'
        ? {
            mode: 'per_rule',
            flatAmount: '',
            rules: cover.rules,
            tableGuid: cover.tableGuid,
            nodeGuid: cover.nodeGuid,
            tableVersion: cover.tableVersion
          }
        : cover.probableMode === 'per_rule'
        ? { mode: 'per_rule', flatAmount: '', rules: [], hintOnly: true }
        : {
            mode: 'all',
            flatAmount: fromCents(benefit.benefit_amount_in_cents),
            rules: []
          },
    waiting:
      waiting.mode === 'per_rule'
        ? {
            mode: 'per_rule',
            flatCardinality: '',
            flatGranularity: benefit.waiting_period_granularity || 'Day',
            rules: waiting.rules,
            ruleGranularity: benefit.waiting_period_granularity || 'Day',
            tableGuid: waiting.tableGuid,
            nodeGuid: waiting.nodeGuid,
            tableVersion: waiting.tableVersion
          }
        : waiting.probableMode === 'per_rule'
        ? {
            mode: 'per_rule',
            flatCardinality: '',
            flatGranularity: benefit.waiting_period_granularity || 'Day',
            rules: [],
            ruleGranularity: benefit.waiting_period_granularity || 'Day',
            hintOnly: true
          }
        : {
            mode: 'all',
            flatCardinality: fmtVal(benefit.waiting_period_cardinality),
            flatGranularity: benefit.waiting_period_granularity || 'Day',
            rules: [],
            ruleGranularity: 'Day'
          },
    premiumSkipped: !hasPremium,
    premium:
      premium.mode === 'per_rule'
        ? {
            mode: 'per_rule',
            flatAmount: '',
            rules: premium.rules,
            tableGuid: premium.tableGuid,
            nodeGuid: premium.nodeGuid,
            tableVersion: premium.tableVersion
          }
        : premium.probableMode === 'per_rule'
        ? { mode: 'per_rule', flatAmount: '', rules: [], hintOnly: true }
        : {
            mode: 'all',
            flatAmount: fromCents(benefit.premium_amount_in_cents),
            rules: []
          },
    premiumCardinality: fmtVal(benefit.premium_cardinality),
    premiumGranularity: benefit.premium_granularity || 'Month'
  }
}

const defaultTargetState = () => ({
  mode: 'all', // 'all' | 'per_rule'
  flatAmount: '',
  rules: [] // [{ id, relationships: [], amount: '' }]
})

const defaultWaitingState = () => ({
  mode: 'all',
  flatCardinality: '',
  flatGranularity: 'Day',
  rules: [], // [{ id, relationships: [], cardinality: '' }]
  ruleGranularity: 'Day'
})

// Every relationship chosen in Stage 1 must appear in exactly one rule
// before "Per rule" is allowed to submit — §5.3, no partial coverage.
function validateRuleCoverage(allowedRelationshipCodes, rules) {
  const seen = {}
  const duplicates = []
  rules.forEach(rule => {
    rule.relationships.forEach(r => {
      if (seen[r]) duplicates.push(r)
      seen[r] = true
    })
  })
  const uncovered = allowedRelationshipCodes.filter(r => !seen[r])
  return { uncovered, duplicates }
}

// One rule row: a relationship multi-select + a value field. Shared by
// Cover/Waiting/Premium's "Per rule" branch (§4).
function RuleRow({
  rule,
  availableRelationships,
  valueLabel,
  currency,
  onChange,
  onRemove
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
      <FormControl size="small" sx={{ flex: 2 }}>
        <InputLabel>Relationships</InputLabel>
        <Select
          multiple
          value={rule.relationships}
          input={<OutlinedInput label="Relationships" />}
          renderValue={sel => sel.join(', ')}
          onChange={e => {
            const value = e.target.value
            onChange({
              ...rule,
              relationships:
                typeof value === 'string' ? value.split(',') : value
            })
          }}
        >
          {availableRelationships.length === 0 && (
            <MenuItem disabled value="">
              No relationships left to assign — add some in Stage 1
            </MenuItem>
          )}
          {availableRelationships.map(r => (
            <MenuItem key={r} value={r}>
              <Checkbox
                checked={rule.relationships.indexOf(r) > -1}
                size="small"
              />
              <ListItemText primary={r} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        type="number"
        label={valueLabel}
        value={rule.amount !== undefined ? rule.amount : rule.cardinality}
        onChange={e => {
          const key = rule.amount !== undefined ? 'amount' : 'cardinality'
          onChange({ ...rule, [key]: e.target.value })
        }}
        InputProps={
          currency
            ? {
                startAdornment: (
                  <InputAdornment position="start">{currency}</InputAdornment>
                )
              }
            : {}
        }
        sx={{ flex: 1 }}
      />
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{ color: '#c62828', mt: 0.5 }}
      >
        <Delete fontSize="small" />
      </IconButton>
    </Box>
  )
}

// Read-only display of ratingNodeSummary.js's output — the actual current
// rules for a non-decompilable target, since it can describe any table_type/
// dimensions/expression combo (unlike the strict wizard-shape decompiler).
function RatingConfigSummary({ summary }) {
  if (!summary) return null
  return (
    <Box
      sx={{
        border: '1px solid #e5e7eb',
        borderRadius: 1.5,
        p: 1.5,
        mb: 2,
        backgroundColor: '#fafafa'
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: '#6B7280',
          textTransform: 'uppercase',
          display: 'block',
          mb: 0.5
        }}
      >
        Current configuration (read-only)
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Base: {summary.base}
      </Typography>
      {summary.tables.map((table, i) =>
        table ? (
          <Box key={table.guid || i} sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {`Table ${i + 1}: "${table.name}" — ${table.tableTypeLabel}`}
            </Typography>
            {table.dimensions.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                Dimensions: {table.dimensions.join(', ')}
              </Typography>
            )}
            <Table size="small" sx={{ mt: 0.5 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ fontSize: 11, fontWeight: 700, color: '#6B7280' }}
                  >
                    Condition
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: 11, fontWeight: 700, color: '#6B7280' }}
                  >
                    Value
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {table.rows.map((row, j) => (
                  <TableRow key={j}>
                    <TableCell sx={{ fontSize: 12 }}>{row.condition}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
            >
              {table.noMatchLabel}
            </Typography>
          </Box>
        ) : (
          <Alert key={i} severity="error" sx={{ mb: 1 }}>
            {`Table ${i +
              1} is referenced but no longer exists (dangling reference).`}
          </Alert>
        )
      )}
    </Box>
  )
}

// A full Cover/Premium-shaped "All vs Per rule" editor.
function MoneyTargetEditor({
  label,
  state,
  onChange,
  allowedRelationships,
  currency,
  optional,
  onSkip,
  skipped,
  summary
}) {
  const isUntouchedHint = Boolean(state.hintOnly) && state.rules.length === 0
  const { uncovered, duplicates } =
    state.mode === 'per_rule' && !isUntouchedHint
      ? validateRuleCoverage(allowedRelationships, state.rules)
      : { uncovered: [], duplicates: [] }

  if (optional && skipped) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: 'italic' }}
        >
          Skipped — this benefit adds no premium of its own. The package's own
          premium still applies as normal.
        </Typography>
        <Button
          size="small"
          sx={{ textTransform: 'none', mt: 1 }}
          onClick={() => onSkip(false)}
        >
          Add a premium for this benefit
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ mb: 2 }}>
      {optional && (
        <Button
          size="small"
          sx={{ textTransform: 'none', mb: 1, color: '#6b7280' }}
          onClick={() => onSkip(true)}
        >
          Skip — no premium for this benefit
        </Button>
      )}
      <RadioGroup
        row
        value={state.mode}
        onChange={e => onChange({ ...state, mode: e.target.value })}
        sx={{ mb: 1 }}
      >
        <FormControlLabel
          value="all"
          control={<Radio size="small" />}
          label="Same for all relationships"
        />
        <FormControlLabel
          value="per_rule"
          control={<Radio size="small" />}
          label="Select relationship per rule"
        />
      </RadioGroup>

      {state.mode === 'all' ? (
        <TextField
          size="small"
          type="number"
          label={label}
          value={state.flatAmount}
          onChange={e => onChange({ ...state, flatAmount: e.target.value })}
          InputProps={
            currency
              ? {
                  startAdornment: (
                    <InputAdornment position="start">{currency}</InputAdornment>
                  )
                }
              : {}
          }
          sx={{ width: 240 }}
        />
      ) : (
        <Box>
          {isUntouchedHint && (
            <>
              <RatingConfigSummary summary={summary} />
              <Alert severity="info" sx={{ mb: 1 }}>
                The rules above are shown for reference only and can't be edited
                here directly — leaving this empty keeps them untouched. Add a
                rule below only if you want to replace them entirely.
              </Alert>
            </>
          )}
          {state.rules.map(rule => (
            <RuleRow
              key={rule.id}
              rule={rule}
              availableRelationships={allowedRelationships}
              valueLabel={label}
              currency={currency}
              onChange={next =>
                onChange({
                  ...state,
                  rules: state.rules.map(r => (r.id === rule.id ? next : r))
                })
              }
              onRemove={() =>
                onChange({
                  ...state,
                  rules: state.rules.filter(r => r.id !== rule.id)
                })
              }
            />
          ))}
          <Button
            size="small"
            startIcon={<Add />}
            sx={{ textTransform: 'none' }}
            onClick={() =>
              onChange({
                ...state,
                rules: state.rules.concat({
                  id: uid(),
                  relationships: [],
                  amount: ''
                })
              })
            }
          >
            Add rule
          </Button>
          {duplicates.length > 0 && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {`Each relationship can only appear in one rule. Duplicated: ${[
                ...new Set(duplicates)
              ].join(', ')}`}
            </Alert>
          )}
          {uncovered.length > 0 && duplicates.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {`Not covered by any rule yet: ${uncovered.join(', ')}`}
            </Alert>
          )}
        </Box>
      )}
    </Box>
  )
}

const AddBenefitWizard = props => {
  const currency = props.currency
  const isLoading = props.isLoading || props.chainLoader || false
  const isEdit = Boolean(props.isEdit && props.initialData)
  // Computed once for this mount's lazy useState initializers (a fresh
  // AddBenefitWizard instance is mounted per modal open — PackagesTab.js
  // keys it on the selected benefit — so this never needs to react to a
  // later prop change).
  const editState = isEdit
    ? deriveEditState(props.initialData, props.ratingNodes, props.ratingTables)
    : null

  const [step, setStep] = useState(0)
  const [name, setName] = useState(() => (editState ? editState.name : ''))
  const [benefitType, setBenefitType] = useState(() =>
    editState ? editState.benefitType : ''
  )
  const [allowedRelationships, setAllowedRelationships] = useState(() =>
    editState ? editState.allowedRelationships : []
  )
  const [newRel, setNewRel] = useState(DEFAULT_NEW_REL)
  const [cover, setCover] = useState(() =>
    editState ? editState.cover : defaultTargetState()
  )
  const [benefitCardinality, setBenefitCardinality] = useState(() =>
    editState ? editState.benefitCardinality : ''
  )
  const [benefitGranularity, setBenefitGranularity] = useState(() =>
    editState ? editState.benefitGranularity : 'Month'
  )
  const [waiting, setWaiting] = useState(() =>
    editState ? editState.waiting : defaultWaitingState()
  )
  const [premium, setPremium] = useState(() =>
    editState ? editState.premium : defaultTargetState()
  )
  const [premiumSkipped, setPremiumSkipped] = useState(() =>
    editState ? editState.premiumSkipped : true
  )
  const [premiumCardinality, setPremiumCardinality] = useState(() =>
    editState ? editState.premiumCardinality : ''
  )
  const [premiumGranularity, setPremiumGranularity] = useState(() =>
    editState ? editState.premiumGranularity : 'Month'
  )
  const [error, setError] = useState('')
  // Retry checkpoint (§5.5) — the last chain attempt's per-target progress,
  // so a re-submit after a partial failure skips whatever already succeeded.
  const [lastAttemptTargets, setLastAttemptTargets] = useState(null)

  const usedRelationships = allowedRelationships.map(r => r.relationship)
  const availableToAdd = RELATIONSHIP_TYPES.filter(
    r => !usedRelationships.includes(r)
  )
  const relationshipCodes = allowedRelationships.map(r => r.relationship)

  // Same completeness bar as the auto-commit-on-Next path (pendingRelIfValid,
  // below) — the explicit Add button shouldn't accept a row Next would reject.
  const handleAddRelationship = () => {
    const pending = pendingRelIfValid()
    if (!pending) return
    setAllowedRelationships(prev => prev.concat([pending]))
    setNewRel(DEFAULT_NEW_REL)
  }
  const handleRemoveRelationship = index => {
    setAllowedRelationships(prev => prev.filter((_, i) => i !== index))
  }
  const handleRelFieldChange = (index, field, value) => {
    setAllowedRelationships(prev =>
      prev.map((rel, i) => (i === index ? { ...rel, [field]: value } : rel))
    )
  }

  const steps = ['Basic Info', 'Benefit Amount', 'Waiting Period', 'Premium']

  // A relationship typed into the "add new" row but not yet committed via its
  // own Add button shouldn't block Next — auto-include it if it's valid on
  // its own, so the user isn't forced into two separate clicks to do one thing.
  const pendingRelIfValid = () => {
    if (!newRel.relationship) return null
    if (newRel.min_age === '' || newRel.max_age === '') return null
    if (parseInt(newRel.max_age) <= parseInt(newRel.min_age || 0)) return null
    return newRel
  }
  const effectiveRelationships = () => {
    const pending = pendingRelIfValid()
    return pending
      ? allowedRelationships.concat([pending])
      : allowedRelationships
  }

  const validateStep1 = () => {
    if (!name.trim()) return 'Benefit name is required'
    const rels = effectiveRelationships()
    if (rels.length === 0) return 'At least one relationship is required'
    for (let i = 0; i < rels.length; i++) {
      const r = rels[i]
      if (r.min_age === '' || r.max_age === '')
        return `${r.relationship}: min/max age are required`
      if (parseInt(r.max_age) <= parseInt(r.min_age || 0))
        return `${r.relationship}: max age must exceed min age`
    }
    return ''
  }

  // Commits a still-pending, valid "add new relationship" row into the real
  // list — called when advancing past Stage 1, so the user never has to
  // click Add separately from Next just to make one entry count.
  const commitPendingRelationship = () => {
    const pending = pendingRelIfValid()
    if (pending) {
      setAllowedRelationships(prev => prev.concat([pending]))
      setNewRel(DEFAULT_NEW_REL)
    }
  }

  const validateMoneyTarget = (state, label) => {
    if (state.mode === 'all') {
      if (state.flatAmount === '' || Number(state.flatAmount) <= 0)
        return `${label}: amount is required`
      return ''
    }
    // A hint-only "Per rule" pre-selection (§9.2 — a non-decompilable existing
    // table, guessed from its presence alone) is allowed to stay empty; it
    // means "leave the real table untouched," not "an empty table to save."
    if (state.hintOnly && state.rules.length === 0) return ''
    if (state.rules.length === 0) return `${label}: add at least one rule`
    for (let i = 0; i < state.rules.length; i++) {
      const r = state.rules[i]
      if (r.relationships.length === 0)
        return `${label}: every rule needs at least one relationship`
      if (r.amount === '' || Number(r.amount) < 0)
        return `${label}: every rule needs an amount`
    }
    const { uncovered, duplicates } = validateRuleCoverage(
      relationshipCodes,
      state.rules
    )
    if (duplicates.length)
      return `${label}: ${[...new Set(duplicates)].join(
        ', '
      )} appear in more than one rule`
    if (uncovered.length)
      return `${label}: ${uncovered.join(', ')} not covered by any rule`
    return ''
  }

  const validateWaiting = () => {
    if (waiting.mode === 'all') {
      if (
        waiting.flatCardinality === '' ||
        Number(waiting.flatCardinality) < 0
      ) {
        return 'Waiting Period: cardinality is required'
      }
      return ''
    }
    if (waiting.hintOnly && waiting.rules.length === 0) return ''
    if (waiting.rules.length === 0)
      return 'Waiting Period: add at least one rule'
    for (let i = 0; i < waiting.rules.length; i++) {
      const r = waiting.rules[i]
      if (r.relationships.length === 0)
        return 'Waiting Period: every rule needs at least one relationship'
      if (r.cardinality === '' || Number(r.cardinality) < 0)
        return 'Waiting Period: every rule needs a cardinality'
    }
    const { uncovered, duplicates } = validateRuleCoverage(
      relationshipCodes,
      waiting.rules
    )
    if (duplicates.length)
      return `Waiting Period: ${[...new Set(duplicates)].join(
        ', '
      )} appear in more than one rule`
    if (uncovered.length)
      return `Waiting Period: ${uncovered.join(', ')} not covered by any rule`
    return ''
  }

  const validateStep = i => {
    if (i === 0) return validateStep1()
    if (i === 1) {
      // benefit_cardinality/benefit_granularity are required on the DTO
      // (@IsNumber/@IsEnum, no @IsOptional) regardless of All vs Per rule.
      if (benefitCardinality === '' || parseInt(benefitCardinality) <= 0) {
        return 'Benefit Amount: cardinality is required'
      }
      return validateMoneyTarget(cover, 'Benefit Amount')
    }
    if (i === 2) return validateWaiting()
    if (i === 3) {
      if (premiumSkipped) return ''
      return validateMoneyTarget(premium, 'Premium')
    }
    return ''
  }

  const handleNext = () => {
    const err = validateStep(step)
    if (err) {
      setError(err)
      return
    }
    if (step === 0) commitPendingRelationship()
    setError('')
    setStep(s => Math.min(s + 1, steps.length - 1))
  }
  const handleBack = () => {
    setError('')
    setStep(s => Math.max(s - 1, 0))
  }

  // Compiles one "Per rule" target into its RatingTable + RatingNode create
  // payloads (§5.1). `valueOf(rule)` extracts the cell value from a rule
  // (amount for money targets, cardinality for waiting period).
  const compileTarget = (
    key,
    fkField,
    appliesTo,
    nodeType,
    tableType,
    name_,
    benefitCode,
    packageCode,
    rules,
    valueOf,
    extraNodeFields
  ) => {
    const segments = rules.map((rule, i) => ({
      label: `rule_${i}`,
      expression: buildRelationshipExpression(rule.relationships)
    }))
    const cells = rules.map((rule, i) => ({
      segment_refs: [`rule_${i}`],
      value_in_cents: valueOf(rule)
    }))
    return {
      key,
      fkField,
      tablePayload: {
        code: `${benefitCode}-${key}`,
        name: `${name_} — ${key}`,
        table_type: tableType,
        dimensions: ['dependant.relationship'],
        segments,
        cells,
        no_match_action: 'leave_unchanged'
      },
      nodePayload: Object.assign(
        {
          package_code: packageCode,
          benefit_code: benefitCode,
          node_type: nodeType,
          applies_to: appliesTo,
          label: `${name_} ${key}`
        },
        extraNodeFields || {}
      )
    }
  }

  // §9.3 save-semantics matrix — decides, per target, whether editing needs
  // no rating call (flat throughout), a fresh create (All → Per rule), the
  // FK cleared (Per rule → All, table/node left orphaned per §9.5), or an
  // update of the existing table in place (Per rule → Per rule).
  const planEditTarget = (
    key,
    fkField,
    before,
    afterMode,
    rules,
    valueOf,
    extraNodeFields,
    appliesTo,
    nodeType,
    tableType,
    benefitCode,
    packageCode
  ) => {
    const wasPerRule = before.mode === 'per_rule'
    const isPerRule = afterMode === 'per_rule'
    if (!wasPerRule && !isPerRule) return { action: 'none' }
    // Hint-only "Per rule" (probableMode guessed it from a non-decompilable
    // table, no real tableGuid to update — see deriveEditState/§9.2) left with
    // no rules added is untouched, not an empty table to save. Once the user
    // actually adds a rule, treat it exactly like All → Per rule: build a
    // fresh table+node from scratch (the old hand-built one is orphaned, same
    // as any other Per rule → All transition, §9.5) rather than guessing at
    // a PUT against unknown existing content.
    if (before.hintOnly && isPerRule && rules.length === 0)
      return { action: 'none' }
    if ((!wasPerRule || before.hintOnly) && isPerRule) {
      return {
        action: 'create',
        target: compileTarget(
          key,
          fkField,
          appliesTo,
          nodeType,
          tableType,
          name,
          benefitCode,
          packageCode,
          rules,
          valueOf,
          extraNodeFields
        )
      }
    }
    if (wasPerRule && !isPerRule) {
      return { action: 'clear', fkField }
    }
    const segments = rules.map((rule, i) => ({
      label: `rule_${i}`,
      expression: buildRelationshipExpression(rule.relationships)
    }))
    const cells = rules.map((rule, i) => ({
      segment_refs: [`rule_${i}`],
      value_in_cents: valueOf(rule)
    }))
    return {
      action: 'update',
      fkField,
      nodeGuid: before.nodeGuid,
      target: {
        key,
        tableGuid: before.tableGuid,
        tableVersion: before.tableVersion,
        tablePayload: { segments, cells }
      }
    }
  }

  const handleSubmit = () => {
    for (let i = 0; i < steps.length; i++) {
      const err = validateStep(i)
      if (err) {
        setStep(i)
        setError(err)
        return
      }
    }
    setError('')

    const benefitGuid = uid() + uid() // not a real guid, just needs to be unique+stable for this submission's benefit_code
    const benefitCode = makeBenefitCode(name, benefitGuid)
    const packageCode = props.packageCode

    const benefitPayload = {
      name: name.trim(),
      benefit_kind: 'money',
      benefit_type: benefitType || undefined,
      benefit_cardinality: parseInt(benefitCardinality) || 1,
      benefit_granularity: benefitGranularity,
      allowed_relationships: allowedRelationships.map(r => ({
        relationship: r.relationship,
        min_age: parseInt(r.min_age) || 0,
        max_age: parseInt(r.max_age) || 0,
        max_count: parseInt(r.max_count) || 0
      }))
    }

    if (cover.mode === 'all') {
      benefitPayload.benefit_amount_in_cents = toCents(cover.flatAmount)
    }
    if (waiting.mode === 'all') {
      benefitPayload.waiting_period_cardinality =
        parseInt(waiting.flatCardinality) || 0
      benefitPayload.waiting_period_granularity = waiting.flatGranularity
    } else {
      // Mutual exclusivity (§5.2) — the per-rule node's own base_waiting_period
      // is authoritative; the flat field must not carry a stale value.
      benefitPayload.waiting_period_cardinality = 0
      benefitPayload.waiting_period_granularity = waiting.ruleGranularity
    }
    if (!premiumSkipped) {
      if (premium.mode === 'all') {
        benefitPayload.premium_amount_in_cents = toCents(premium.flatAmount)
      }
      // premium_cardinality/premium_granularity are optional on the DTO —
      // only send them when the user actually set a cardinality.
      if (premiumCardinality !== '') {
        benefitPayload.premium_cardinality = parseInt(premiumCardinality) || 1
        benefitPayload.premium_granularity = premiumGranularity
      }
    }

    if (!isEdit) {
      const targets = []
      if (cover.mode === 'per_rule') {
        targets.push(
          compileTarget(
            'cover',
            'cover_rating_node_guid',
            'cover',
            'multi_factor_rating',
            'base_rate',
            name,
            benefitCode,
            packageCode,
            cover.rules,
            r => toCents(r.amount),
            { base_amount_in_cents: 0 }
          )
        )
      }
      if (waiting.mode === 'per_rule') {
        targets.push(
          compileTarget(
            'waiting_period',
            'waiting_period_rating_node_guid',
            'waiting_period',
            'waiting_period_rating',
            'base_rate',
            name,
            benefitCode,
            packageCode,
            waiting.rules,
            r => Number(r.cardinality) || 0,
            {
              base_waiting_period: {
                cardinality: 0,
                granularity: waiting.ruleGranularity
              }
            }
          )
        )
      }
      if (!premiumSkipped && premium.mode === 'per_rule') {
        targets.push(
          compileTarget(
            'premium',
            'premium_rating_node_guid',
            'premium',
            'multi_factor_rating',
            'base_rate',
            name,
            benefitCode,
            packageCode,
            premium.rules,
            r => toCents(r.amount),
            { base_amount_in_cents: 0 }
          )
        )
      }

      // Merge any checkpointed guids from a prior failed attempt (§5.5) —
      // skip re-creating whatever already succeeded.
      const mergedTargets = targets.map(t => {
        const prior =
          lastAttemptTargets && lastAttemptTargets.find(p => p.key === t.key)
        return prior
          ? Object.assign({}, t, {
              tableGuid: prior.tableGuid,
              nodeGuid: prior.nodeGuid
            })
          : t
      })

      if (mergedTargets.length === 0) {
        if (props.onSubmit) props.onSubmit(benefitPayload)
        return
      }
      if (props.onSubmitWithRules) {
        props.onSubmitWithRules({ targets: mergedTargets, benefitPayload })
      }
      return
    }

    // Edit path (§9.3).
    const plans = [
      planEditTarget(
        'cover',
        'cover_rating_node_guid',
        editState.cover,
        cover.mode,
        cover.rules,
        r => toCents(r.amount),
        { base_amount_in_cents: 0 },
        'cover',
        'multi_factor_rating',
        'base_rate',
        benefitCode,
        packageCode
      ),
      planEditTarget(
        'waiting_period',
        'waiting_period_rating_node_guid',
        editState.waiting,
        waiting.mode,
        waiting.rules,
        r => Number(r.cardinality) || 0,
        {
          base_waiting_period: {
            cardinality: 0,
            granularity: waiting.ruleGranularity
          }
        },
        'waiting_period',
        'waiting_period_rating',
        'base_rate',
        benefitCode,
        packageCode
      ),
      planEditTarget(
        'premium',
        'premium_rating_node_guid',
        editState.premium,
        premiumSkipped ? 'all' : premium.mode,
        premium.rules,
        r => toCents(r.amount),
        { base_amount_in_cents: 0 },
        'premium',
        'multi_factor_rating',
        'base_rate',
        benefitCode,
        packageCode
      )
    ]

    const createTargets = plans
      .filter(p => p.action === 'create')
      .map(p => p.target)
    const updateTargets = plans
      .filter(p => p.action === 'update')
      .map(p => p.target)
    const clearedFkFields = plans
      .filter(p => p.action === 'clear')
      .map(p => p.fkField)
    // "Still per_rule" targets keep their FK unchanged — a full PUT still
    // needs it sent explicitly (matches BenefitsForm's existing convention).
    plans
      .filter(p => p.action === 'update')
      .forEach(p => {
        benefitPayload[p.fkField] = p.nodeGuid
      })
    clearedFkFields.forEach(fkField => {
      benefitPayload[fkField] = null
    })

    // Merge checkpointed guids from a prior failed retry, same as create.
    const mergedCreateTargets = createTargets.map(t => {
      const prior =
        lastAttemptTargets && lastAttemptTargets.find(p => p.key === t.key)
      return prior
        ? Object.assign({}, t, {
            tableGuid: prior.tableGuid,
            nodeGuid: prior.nodeGuid
          })
        : t
    })

    if (mergedCreateTargets.length === 0 && updateTargets.length === 0) {
      if (props.onUpdateBenefit) props.onUpdateBenefit(benefitPayload)
      return
    }
    if (props.onUpdateBenefitWithRules) {
      props.onUpdateBenefitWithRules({
        createTargets: mergedCreateTargets,
        updateTargets,
        benefitPayload
      })
    }
  }

  // Called by the parent when a chain attempt fails — keeps whatever
  // succeeded so the next submit can resume instead of restarting (§5.5).
  // Create-chain progress carries `targets`; edit-chain progress (only ever
  // has create-type actions to checkpoint — update-type ones always target
  // an already-existing table, nothing to resume) carries `createTargets`.
  React.useEffect(() => {
    if (!props.chainError || !props.chainProgress) return
    const checkpointed =
      props.chainProgress.targets || props.chainProgress.createTargets
    if (checkpointed) setLastAttemptTargets(checkpointed)
  }, [props.chainError, props.chainProgress])

  const TH_STYLE = {
    fontSize: 11,
    fontWeight: 700,
    color: '#6B7280',
    py: 1,
    px: 1
  }
  const TD_STYLE = { py: 0.5, px: 1 }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {isEdit ? 'Edit Benefit' : 'Add New Benefit'}
      </Typography>
      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {props.chainError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {`Rating setup failed: ${props.chainError}. Whatever already succeeded is kept — submitting again will only retry what failed.`}
        </Alert>
      )}
      {props.chainLoader && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Setting up rating tables and nodes…
        </Alert>
      )}
      {/* §9.4 — an edit to an existing Per-rule table can come back pending
          approval; the rules shown above still reflect the pre-edit values
          until it's approved, so this banner is the only signal it changed. */}
      {props.chainPending && props.chainPending.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {`Pending approval for: ${props.chainPending
            .map(p => p.key)
            .join(
              ', '
            )}. The values shown reflect what's currently live, not your edit, until it's approved.`}
        </Alert>
      )}
      {isEdit && editState.notDecompilable.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {`${editState.notDecompilable.join(', ')} ${
            editState.notDecompilable.length === 1 ? 'is' : 'are'
          } currently priced via a custom rating table this wizard can't fully represent — shown blank/flat below, not the real values. Leave that stage untouched unless you mean to replace its pricing entirely.`}
        </Alert>
      )}

      {step === 0 && (
        <Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Benefit Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Life Cover"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Benefit Type (optional)
            </Typography>
            <FormControl fullWidth>
              <Select
                value={benefitType}
                onChange={e => setBenefitType(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Unset</em>
                </MenuItem>
                {BENEFIT_TYPES.map(t => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500 }}>
              Allowed Relationships
            </Typography>
            {allowedRelationships.length > 0 && (
              <Box
                sx={{
                  mb: 2,
                  border: '1px solid #e5e7eb',
                  borderRadius: 1,
                  overflow: 'hidden'
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                      <TableCell sx={TH_STYLE}>Relationship</TableCell>
                      <TableCell sx={TH_STYLE}>Min Age</TableCell>
                      <TableCell sx={TH_STYLE}>Max Age</TableCell>
                      <TableCell sx={TH_STYLE}>Max Count</TableCell>
                      <TableCell sx={{ ...TH_STYLE, width: 40 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allowedRelationships.map((rel, index) => (
                      <TableRow key={index}>
                        <TableCell sx={TD_STYLE}>{rel.relationship}</TableCell>
                        <TableCell sx={TD_STYLE}>
                          <TextField
                            size="small"
                            type="number"
                            value={rel.min_age}
                            onChange={e =>
                              handleRelFieldChange(
                                index,
                                'min_age',
                                e.target.value
                              )
                            }
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell sx={TD_STYLE}>
                          <TextField
                            size="small"
                            type="number"
                            value={rel.max_age}
                            onChange={e =>
                              handleRelFieldChange(
                                index,
                                'max_age',
                                e.target.value
                              )
                            }
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell sx={TD_STYLE}>
                          <TextField
                            size="small"
                            type="number"
                            value={rel.max_count}
                            onChange={e =>
                              handleRelFieldChange(
                                index,
                                'max_count',
                                e.target.value
                              )
                            }
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell sx={TD_STYLE}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveRelationship(index)}
                            sx={{ color: '#ef4444' }}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            {availableToAdd.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 80px 80px 80px auto',
                  gap: 1
                }}
              >
                <FormControl fullWidth size="small">
                  <Select
                    value={newRel.relationship}
                    displayEmpty
                    onChange={e =>
                      setNewRel({ ...newRel, relationship: e.target.value })
                    }
                  >
                    <MenuItem value="" disabled>
                      Select relationship
                    </MenuItem>
                    {availableToAdd.map(r => (
                      <MenuItem key={r} value={r}>
                        {r}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="number"
                  placeholder="Min age"
                  value={newRel.min_age}
                  onChange={e =>
                    setNewRel({ ...newRel, min_age: e.target.value })
                  }
                />
                <TextField
                  size="small"
                  type="number"
                  placeholder="Max age"
                  value={newRel.max_age}
                  onChange={e =>
                    setNewRel({ ...newRel, max_age: e.target.value })
                  }
                />
                <TextField
                  size="small"
                  type="number"
                  placeholder="Count"
                  value={newRel.max_count}
                  onChange={e =>
                    setNewRel({ ...newRel, max_count: e.target.value })
                  }
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddRelationship}
                  disabled={!pendingRelIfValid()}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  Add
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {step === 1 && (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              mb: 2,
              maxWidth: 480
            }}
          >
            <TextField
              size="small"
              type="number"
              label="Cardinality"
              value={benefitCardinality}
              onChange={e => setBenefitCardinality(e.target.value)}
            />
            <FormControl size="small">
              <InputLabel>Granularity</InputLabel>
              <Select
                label="Granularity"
                value={benefitGranularity}
                onChange={e => setBenefitGranularity(e.target.value)}
              >
                <MenuItem value="Month">Month</MenuItem>
                <MenuItem value="Year">Year</MenuItem>
                <MenuItem value="Event">Event (once)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <MoneyTargetEditor
            label={`Benefit Amount${currency ? ` (${currency})` : ''}`}
            state={cover}
            onChange={setCover}
            allowedRelationships={relationshipCodes}
            currency={currency}
            summary={isEdit ? editState.summaries.cover : null}
          />
        </Box>
      )}

      {step === 2 && (
        <Box sx={{ mb: 2 }}>
          <RadioGroup
            row
            value={waiting.mode}
            onChange={e => setWaiting({ ...waiting, mode: e.target.value })}
            sx={{ mb: 1 }}
          >
            <FormControlLabel
              value="all"
              control={<Radio size="small" />}
              label="Same for all relationships"
            />
            <FormControlLabel
              value="per_rule"
              control={<Radio size="small" />}
              label="Select relationship per rule"
            />
          </RadioGroup>
          {waiting.mode === 'all' ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
                maxWidth: 480
              }}
            >
              <TextField
                size="small"
                type="number"
                label="Cardinality"
                placeholder="0"
                value={waiting.flatCardinality}
                onChange={e =>
                  setWaiting({ ...waiting, flatCardinality: e.target.value })
                }
                helperText="Enter 0 for no waiting period"
              />
              <FormControl size="small">
                <InputLabel>Granularity</InputLabel>
                <Select
                  label="Granularity"
                  value={waiting.flatGranularity}
                  onChange={e =>
                    setWaiting({ ...waiting, flatGranularity: e.target.value })
                  }
                >
                  <MenuItem value="Day">Day</MenuItem>
                  <MenuItem value="Week">Week</MenuItem>
                  <MenuItem value="Month">Month</MenuItem>
                  <MenuItem value="Year">Year</MenuItem>
                </Select>
              </FormControl>
            </Box>
          ) : (
            <Box>
              <FormControl size="small" sx={{ mb: 2, width: 200 }}>
                <InputLabel>Granularity</InputLabel>
                <Select
                  label="Granularity"
                  value={waiting.ruleGranularity}
                  onChange={e =>
                    setWaiting({ ...waiting, ruleGranularity: e.target.value })
                  }
                >
                  <MenuItem value="Day">Day</MenuItem>
                  <MenuItem value="Week">Week</MenuItem>
                  <MenuItem value="Month">Month</MenuItem>
                  <MenuItem value="Year">Year</MenuItem>
                </Select>
                <FormHelperText>Applies to every rule below</FormHelperText>
              </FormControl>
              {Boolean(waiting.hintOnly) && waiting.rules.length === 0 && (
                <>
                  <RatingConfigSummary
                    summary={isEdit ? editState.summaries.waiting_period : null}
                  />
                  <Alert severity="info" sx={{ mb: 1 }}>
                    The rules above are shown for reference only and can't be
                    edited here directly — leaving this empty keeps them
                    untouched. Add a rule below only if you want to replace them
                    entirely.
                  </Alert>
                </>
              )}
              {waiting.rules.map(rule => (
                <RuleRow
                  key={rule.id}
                  rule={rule}
                  availableRelationships={relationshipCodes}
                  valueLabel="Cardinality"
                  onChange={next =>
                    setWaiting({
                      ...waiting,
                      rules: waiting.rules.map(r =>
                        r.id === rule.id ? next : r
                      )
                    })
                  }
                  onRemove={() =>
                    setWaiting({
                      ...waiting,
                      rules: waiting.rules.filter(r => r.id !== rule.id)
                    })
                  }
                />
              ))}
              <Button
                size="small"
                startIcon={<Add />}
                sx={{ textTransform: 'none' }}
                onClick={() =>
                  setWaiting({
                    ...waiting,
                    rules: waiting.rules.concat({
                      id: uid(),
                      relationships: [],
                      cardinality: ''
                    })
                  })
                }
              >
                Add rule
              </Button>
              {(() => {
                const isUntouchedHint =
                  Boolean(waiting.hintOnly) && waiting.rules.length === 0
                const { uncovered, duplicates } = isUntouchedHint
                  ? { uncovered: [], duplicates: [] }
                  : validateRuleCoverage(relationshipCodes, waiting.rules)
                return (
                  <>
                    {duplicates.length > 0 && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {`Each relationship can only appear in one rule. Duplicated: ${[
                          ...new Set(duplicates)
                        ].join(', ')}`}
                      </Alert>
                    )}
                    {uncovered.length > 0 && duplicates.length === 0 && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        {`Not covered by any rule yet: ${uncovered.join(', ')}`}
                      </Alert>
                    )}
                  </>
                )
              })()}
            </Box>
          )}
        </Box>
      )}

      {step === 3 && (
        <Box>
          {!premiumSkipped && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
                mb: 2,
                maxWidth: 480
              }}
            >
              <TextField
                size="small"
                type="number"
                label="Premium Cardinality (optional)"
                value={premiumCardinality}
                onChange={e => setPremiumCardinality(e.target.value)}
              />
              <FormControl size="small">
                <InputLabel>Premium Granularity</InputLabel>
                <Select
                  label="Premium Granularity"
                  value={premiumGranularity}
                  onChange={e => setPremiumGranularity(e.target.value)}
                >
                  <MenuItem value="Month">Month</MenuItem>
                  <MenuItem value="Year">Year</MenuItem>
                  <MenuItem value="Event">Event (once)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
          <MoneyTargetEditor
            label={`Premium Amount${currency ? ` (${currency})` : ''}`}
            state={premium}
            onChange={setPremium}
            allowedRelationships={relationshipCodes}
            currency={currency}
            optional
            skipped={premiumSkipped}
            onSkip={setPremiumSkipped}
            summary={isEdit ? editState.summaries.premium : null}
          />
        </Box>
      )}

      {/* §9.5 — Per rule → All leaves the old table/node orphaned rather than
          auto-deleting them; only shown once every step's data is settled. */}
      {isEdit &&
        step === steps.length - 1 &&
        (() => {
          const orphaning = []
          if (editState.cover.mode === 'per_rule' && cover.mode === 'all')
            orphaning.push('Benefit Amount')
          if (editState.waiting.mode === 'per_rule' && waiting.mode === 'all')
            orphaning.push('Waiting Period')
          if (
            editState.premium.mode === 'per_rule' &&
            (premiumSkipped || premium.mode === 'all')
          )
            orphaning.push('Premium')
          if (orphaning.length === 0) return null
          return (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {`Switching ${orphaning.join(
                ', '
              )} away from "Per rule" won't delete the old rating table/node — they'll stay in the partner's catalogue unused. Clean them up from the Rating Tables/Rating Nodes tabs if you don't need them.`}
            </Alert>
          )
        })()}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          disabled={step === 0}
          onClick={handleBack}
          sx={{ textTransform: 'none' }}
        >
          Back
        </Button>
        {step < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            sx={{
              textTransform: 'none',
              backgroundColor: '#173E4B',
              '&:hover': { backgroundColor: '#0f2d38' }
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={isLoading}
            onClick={handleSubmit}
            sx={{
              textTransform: 'none',
              backgroundColor: '#fba92c',
              '&:hover': { backgroundColor: '#f59e0b' }
            }}
          >
            {isLoading ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Benefit'}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default AddBenefitWizard
